import React, { useState } from "react";
import { Link, useNavigate } from "../router";
import AssetVisual from "../components/AssetVisual";
import PropertyCard from "../components/PropertyCard";
import BankStrip from "../components/BankStrip";
import { properties, ASSET_CLASSES } from "../data/properties";

const PILLARS = [
  {
    no: "01",
    title: "Zero Dead Properties",
    body:
      "No stagnant stock. No disputed parcels. No ghost listings. Every asset carries a Live & Available timestamp and is re-checked on a fixed cadence — the moment a property goes cold or contested, it leaves the platform.",
    proof: "Re-verified weekly",
  },
  {
    no: "02",
    title: "Aditi Legal Shield",
    body:
      "Beyond the seller's paperwork, our in-house legal team runs an exhaustive secondary title check — encumbrance, lineage, litigation, and approvals — before a listing is ever published. You see the Certified Title Check badge only when it's earned.",
    proof: "In-house counsel",
  },
  {
    no: "03",
    title: "Bank-Assisted EMI",
    body:
      "Direct alliances with India's top lenders mean pre-negotiated rates and a priority desk. Model your EMI natively on every asset page and route a pre-qualification file in minutes — no broker chain, no markup.",
    proof: "6 direct alliances",
  },
];

export default function HomePage() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(`/properties${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <>
      {/* ============================ HERO ============================ */}
      <section className="dark" style={{ position: "relative", overflow: "hidden", paddingTop: "clamp(100px,12vw,160px)", paddingBottom: "clamp(100px,12vw,160px)" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.4 }}>
          <AssetVisual tone={["#0B0B0C", "#1b1d24"]} accent="#D4AF37" />
        </div>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, var(--obsidian) 35%, rgba(11,11,12,0.45) 100%)" }} />
        <div className="shell" style={{ position: "relative" }}>
          <span className="eyebrow on-dark reveal">Quiet luxury · Title-vetted · End-to-end</span>
          <h1 className="display reveal" style={{ marginTop: 36, maxWidth: 16 + "ch" }}>
            Own what is
            <br />
            <span className="serif-italic gold-text">unquestionably</span> yours.
          </h1>
          <p className="lead reveal" style={{ marginTop: 36, maxWidth: 580 }}>
            Aditi Realty is a curated brokerage and design-build house for plots,
            villas, houses, land and apartments — where every asset is verified
            twice, financed directly, and delivered without a single loose end.
          </p>

          {/* Smart omnibar */}
          <form onSubmit={submit} className="reveal" style={{ marginTop: 52, maxWidth: 720 }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "rgba(249,249,251,0.05)", border: "1px solid rgba(212,175,55,0.35)",
                borderRadius: 3, padding: 10, backdropFilter: "blur(10px)",
                transition: "all 0.6s var(--ease)",
              }}
            >
              <span style={{ paddingLeft: 14, color: "var(--champagne)", fontSize: "1.1rem", opacity: 0.9 }}>⌕</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search real estate or describe your requirement…"
                style={{
                  flex: 1, background: "transparent", border: "none", outline: "none",
                  color: "var(--paper-ink)", fontSize: "1.0rem", padding: "14px 8px",
                  fontWeight: 300,
                }}
              />
              <button type="submit" className="btn btn-gold" style={{ borderRadius: 2 }}>
                Search <span className="arr">→</span>
              </button>
            </div>
            <div className="flexrow" style={{ marginTop: 20, gap: 12 }}>
              {["Sea-facing villa under ₹8 Cr", "NA land near Bengaluru", "4BR in Jubilee Hills"].map((s) => (
                <button key={s} type="button" onClick={() => setQ(s)} className="tag" style={{ background: "transparent", borderColor: "rgba(249,249,251,0.2)", color: "var(--paper-ink-soft)", cursor: "pointer", transition: "all 0.5s var(--ease)" }}>
                  {s}
                </button>
              ))}
            </div>
          </form>

          <div className="flexrow reveal" style={{ marginTop: 64, gap: 48, flexDirection: "column", alignItems: "flex-start" }}>
            {[["6", "Direct bank alliances"], ["2×", "Title verification"], ["0", "Dead listings, ever"]].map(([n, l]) => (
              <div key={l}>
                <div style={{ fontFamily: "var(--serif)", fontSize: "2.2rem", color: "var(--champagne)" }}>{n}</div>
                <div className="muted" style={{ fontSize: "0.8rem", letterSpacing: "0.04em" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== DUAL PORTAL ENTRY ====================== */}
      <section className="section-sm">
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <PortalCard
              kicker="For Sellers"
              title="List an Asset"
              copy="A premium seller dashboard — upload blueprints and media, set your expected price, and let our desk position it to vetted buyers. No spam, no scattergun calls."
              to="/sell"
              cta="Open Seller Portal"
              dark
            />
            <PortalCard
              kicker="For Buyers"
              title="Acquire an Asset"
              copy="Post your exact requirement, target budget and asset type. Our intent engine matches you to live, title-clean inventory — and only that."
              to="/buy"
              cta="Open Buyer Portal"
            />
          </div>
        </div>
      </section>

      {/* ====================== ASSET CLASSES ====================== */}
      <section className="section">
        <div className="shell">
          <div className="spaced reveal" style={{ marginBottom: 56, flexDirection: "column", alignItems: "flex-start", gap: 32 }}>
            <div>
              <span className="eyebrow">Portfolio diversity</span>
              <h2 className="h-xl" style={{ marginTop: 20 }}>Five asset classes,<br /><span className="serif-italic">one standard.</span></h2>
            </div>
            <Link to="/properties" className="btn-line">View full portfolio <span className="arr">→</span></Link>
          </div>
          <div className="grid asset-row" style={{ gridTemplateColumns: "repeat(5,1fr)" }}>
            {ASSET_CLASSES.map((c, i) => (
              <Link key={c} to={`/properties?class=${c}`} className="card reveal" style={{ overflow: "hidden" }}>
                <div style={{ aspectRatio: "3/4" }}>
                  <AssetVisual tone={properties[i % properties.length].tone} accent="#D4AF37" label={`0${i + 1}`} />
                </div>
                <div style={{ padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "var(--serif)", fontSize: "1.1rem" }}>{c}</span>
                  <span className="gold-text">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== CORE PILLARS ====================== */}
      <section className="section dark">
        <div className="shell">
          <div className="reveal" style={{ marginBottom: 72, maxWidth: 660 }}>
            <span className="eyebrow on-dark">Why Aditi</span>
            <h2 className="h-xl" style={{ marginTop: 22 }}>The three things<br />we refuse to compromise.</h2>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(28px, 3vw, 52px)" }}>
            {PILLARS.map((p) => (
              <div key={p.no} className="reveal" style={{ borderTop: "1px solid var(--obsidian-line)", paddingTop: 32 }}>
                <div className="spaced" style={{ marginBottom: 22, flexDirection: "column", alignItems: "flex-start", gap: 14 }}>
                  <span style={{ fontFamily: "var(--serif)", fontSize: "2.6rem", color: "var(--champagne)", lineHeight: 1 }}>{p.no}</span>
                  <span className="badge"><span className="dot" /> {p.proof}</span>
                </div>
                <h3 className="h-md" style={{ marginBottom: 18 }}>{p.title}</h3>
                <p className="muted" style={{ fontSize: "0.96rem", lineHeight: 1.8 }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== FEATURED INVENTORY ====================== */}
      <section className="section">
        <div className="shell">
          <div className="spaced reveal" style={{ marginBottom: 56, flexDirection: "column", alignItems: "flex-start", gap: 32 }}>
            <div>
              <span className="eyebrow">Live inventory</span>
              <h2 className="h-xl" style={{ marginTop: 20 }}>Currently on the market.</h2>
            </div>
            <Link to="/properties" className="btn-line">See all listings <span className="arr">→</span></Link>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            {properties.slice(0, 3).map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {/* ====================== CONSTRUCTION BAND ====================== */}
      <section className="section dark" style={{ paddingTop: 0 }}>
        <div className="shell">
          <div className="grid reveal" style={{ gridTemplateColumns: "1.05fr 0.95fr", alignItems: "center", gap: "clamp(48px,6vw,96px)" }}>
            <div style={{ aspectRatio: "16/11", borderRadius: 3, overflow: "hidden" }}>
              <AssetVisual tone={["#16130e", "#2c2519"]} accent="#D4AF37" label="Design + Build" />
            </div>
            <div>
              <span className="eyebrow on-dark">End-to-end construction</span>
              <h2 className="h-lg" style={{ marginTop: 22 }}>We don't just sell the land.<br />We build what stands on it.</h2>
              <p className="muted" style={{ marginTop: 24, lineHeight: 1.9 }}>
                From raw parcel to turnkey residence — premium architectural
                engineering, structural design, and custom build, managed by a
                single accountable house. One contract, one timeline, one
                standard of finish.
              </p>
              <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 36 }}>
                {[["Architecture & structural design", "In-house + paneled experts"], ["Turnkey custom build", "Fixed-cost, fixed-timeline"], ["Plot-to-home packages", "Acquire and build together"], ["Quality & milestone audits", "Independent site QA"]].map(([t, s]) => (
                  <div key={t} style={{ borderLeft: "1px solid var(--champagne)", paddingLeft: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{t}</div>
                    <div className="muted" style={{ fontSize: "0.82rem" }}>{s}</div>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn btn-gold" style={{ marginTop: 32 }}>Book a build consultation <span className="arr">→</span></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== BANK ALLIANCES ====================== */}
      <section className="section-sm">
        <div className="shell center reveal">
          <span className="eyebrow gold" style={{ justifyContent: "center" }}>Direct bank alliances</span>
          <h2 className="h-lg" style={{ marginTop: 16, marginBottom: 12 }}>Pre-negotiated home loans, routed directly.</h2>
          <p className="lead maxw-680" style={{ margin: "0 auto 36px" }}>
            We hold direct tie-ups with top-tier lenders. That means priority
            processing, sharper rates, and a pre-qualification flow that starts
            on the listing itself — not in a queue.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}><BankStrip /></div>
        </div>
      </section>

      {/* ====================== INVESTOR CTA ====================== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="shell">
          <div className="reveal" style={{ background: "var(--obsidian)", color: "var(--paper-ink)", borderRadius: 8, padding: "clamp(40px,6vw,84px)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, opacity: 0.35 }}><AssetVisual tone={["#0B0B0C", "#241d10"]} accent="#D4AF37" /></div>
            <div style={{ position: "relative", maxWidth: 620 }}>
              <span className="eyebrow gold">By invitation · Wealth desk</span>
              <h2 className="h-xl" style={{ marginTop: 16 }}>Real estate, run as a portfolio.</h2>
              <p className="muted" style={{ marginTop: 18, fontSize: "1.05rem" }}>
                A private funnel for investors who buy, optimize and flip — built
                around short-to-mid-term ROI, off-market deal flow, and active
                asset management. Discreet by design.
              </p>
              <Link to="/investors" className="btn btn-gold" style={{ marginTop: 30 }}>Request access <span className="arr">→</span></Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .asset-row { grid-template-columns: repeat(2,1fr) !important; }
          .section .grid[style*="repeat(3"] { grid-template-columns: 1fr !important; }
          .grid[style*="1.05fr"] { grid-template-columns: 1fr !important; }
          .grid[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .asset-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

function PortalCard({ kicker, title, copy, to, cta, dark }) {
  return (
    <Link
      to={to}
      className="reveal"
      style={{
        position: "relative", overflow: "hidden", borderRadius: 3,
        padding: "clamp(40px,5vw,64px)", minHeight: 360, display: "flex", flexDirection: "column", justifyContent: "space-between",
        background: dark ? "var(--obsidian)" : "#fff",
        color: dark ? "var(--paper-ink)" : "var(--ink)",
        border: dark ? "none" : "1px solid var(--hairline-ultra)",
        transition: "transform 0.65s var(--ease), box-shadow 0.65s var(--ease), border-color 0.65s var(--ease)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = "0 36px 70px -30px rgba(11,11,12,0.12)"; e.currentTarget.style.borderColor = "var(--hairline-soft)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = ""; }}
    >
      <div>
        <span className={`eyebrow ${dark ? "on-dark" : ""}`}>{kicker}</span>
        <h3 className="h-lg" style={{ marginTop: 24 }}>{title}</h3>
        <p className={dark ? "muted" : "muted"} style={{ marginTop: 20, maxWidth: 400, lineHeight: 1.85 }}>{copy}</p>
      </div>
      <span className="btn-line" style={{ marginTop: 32, alignSelf: "flex-start", color: dark ? "var(--champagne)" : "var(--ink)" }}>
        {cta} <span className="arr">→</span>
      </span>
    </Link>
  );
}
